import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import Highlighter from "react-highlight-words";
import { useHistory } from "react-router-dom";
import { BarLoader } from "react-spinners";
import { useAppContext } from "../../../../../../contexts/app-context/AppContext";
import {
  TeamMemberData,
  useTeams,
} from "../../../../../../contexts/teams-context/TeamsContext";
import { useAppUser } from "../../../../../../contexts/user-context/UserContext";
import Pagination from "../../../../../../shared/components/ui/pagination/Pagination";
import { Country } from "../../../../../../shared/constants/Constant";
import PageHeader from "../../../../components/page-header/PageHeader";

enum MApplicationCountry {
  SELECT_COUNTRY = "",
  ALL = "ALL",
  MALAYSIA = "MALAYSIA",
  SRI_LANKA = "SRI_LANKA",
  BOSNIA = "BOSNIA",
  JAMAICA = "JAMAICA",
  INDONESIA = "INDONESIA",
  INDIA = "INDIA",
}

interface Filter {
  memberName: string;
  country: MApplicationCountry;
}

interface IState {
  members: TeamMemberData[];
  loading: boolean;
  selectedMember: TeamMemberData;
}

const Members = () => {
  const ITEM_COUNT_IN_PAGE: number = 10;
  const initialFilters: Filter = {
    memberName: "",
    country: MApplicationCountry.SELECT_COUNTRY,
  };
  const initialState: IState = {
    members: [],
    loading: false,
    selectedMember: {} as any,
  };
  const initialPagination = {
    total_pages: 0,
    page_number: 0,
  };

  const [filters, setFilters] = useState(initialFilters);
  const [state, setState] = useState(initialState);
  const { members } = useTeams();
  const [pagination, setPagination] = useState(initialPagination);
  const [showPreview] = useState(false);

  const appUser = useAppUser();
  const { push } = useHistory();
  const app = useAppContext();

  useEffect(() => {
    if (state.members.length % ITEM_COUNT_IN_PAGE === 0) {
      const totalPages = state.members.length / ITEM_COUNT_IN_PAGE;
      setPagination((ps) => ({ ...ps, total_pages: totalPages }));
    } else {
      const totalPages =
        Math.floor(state.members.length / ITEM_COUNT_IN_PAGE) + 1;
      setPagination((ps) => ({ ...ps, total_pages: totalPages }));
    }
  }, [state.members]);

  useEffect(() => {
    setPagination((ps) => {
      return { ...ps, page_number: 0 };
    });
  }, [filters.country]);

  useEffect(() => {
    let c_members = members;

    if (!showPreview) {
      if (filters.memberName.trim().length > 0) {
        c_members = c_members.filter((members) =>
          members.fullName
            ?.toLowerCase()
            .includes(filters.memberName.toLowerCase())
        );
      }
      if (filters.country !== MApplicationCountry.ALL) {
        if (filters.country !== MApplicationCountry.SELECT_COUNTRY) {
          c_members = c_members.filter(
            (mem) => mem.country?.toString() === filters.country?.toString()
          );
        }
      }
      setState((ps) => {
        return { ...ps, members: c_members };
      });
    } else {
      setState((ps) => {
        const x = c_members.filter(
          (a) => !!ps.members.find((b) => a.id === b.id)
        );

        return {
          ...ps,
          applications: x,
        };
      });
    }
  }, [filters.memberName, filters.country, members, showPreview]);

  const paginate = (
    array: TeamMemberData[],
    page_number: number,
    page_size: number
  ): TeamMemberData[] =>
    array.slice(page_number * page_size, (page_number + 1) * page_size);

  const getCountryString = (c: Country) => {
    switch (c) {
      case Country.BOSNIA:
        return "Bosnia and Herzegovina";
      case Country.JAMAICA:
        return "Jamaica";
      case Country.MALAYSIA:
        return "Malaysia";
      case Country.INDONESIA:
        return "Indonesia";
      case Country.INDIA:
        return "India";
      default:
        return "Sri Lanka";
    }
  };

  return (
    <div>
      <PageHeader showBackButton={true} sidebarOnly={true} title="Members" />
      <Container
        fluid
        style={{
          paddingTop: "210px",
          paddingLeft: "65px",
          paddingRight: "50px",
          paddingBottom: "10px",
        }}
      >
        <Row>
          <Col
            xs={12}
            md={3}
            xl={3}
            className="d-flex align-items-center ml-md-0 my-1 my-md-0"
          >
            <div style={{ flex: "1" }}>
              <input
                className="app-input input-small search-img"
                style={{ paddingRight: "30px" }}
                placeholder="Member Name"
                value={filters.memberName}
                onChange={(e) => {
                  setFilters({ ...filters, memberName: e.target.value });
                }}
              />
            </div>
          </Col>
          {appUser.userRole.isAdmin && (
            <Col
              xs={12}
              md={3}
              xl={3}
              className="d-flex align-items-center my-1 my-md-0"
              style={{ minWidth: "250px" }}
            >
              <div style={{ flex: "1" }}>
                <select
                  className="app-input input-small filter-img"
                  value={filters.country}
                  onChange={(e) => {
                    setFilters({
                      ...filters,
                      country: e.target.value as any,
                    });
                  }}
                >
                  <option
                    value={MApplicationCountry.SELECT_COUNTRY}
                    disabled
                    hidden
                  >
                    Country
                  </option>
                  <option value={MApplicationCountry.ALL}>All Countries</option>
                  <option value={MApplicationCountry.MALAYSIA}>Malaysia</option>
                  <option value={MApplicationCountry.SRI_LANKA}>
                    Sri Lanka
                  </option>
                  <option value={MApplicationCountry.BOSNIA}>
                    Bosnia and Herzegovina
                  </option>
                  <option value={MApplicationCountry.JAMAICA}>Jamaica</option>
                  <option value={MApplicationCountry.INDONESIA}>Indonesia</option>
                  <option value={MApplicationCountry.INDIA}>India</option>
                </select>
              </div>
            </Col>
          )}
          <Col
            xs={12}
            md={2}
            xl={2}
            className="d-flex align-items-center my-1 my-md-0"
          >
            <div style={{ flex: "1" }}>
              <button
                className="app-button"
                style={{
                  backgroundColor: "#6c757d",
                  padding: "3px 10px 3px 10px",
                }}
                onClick={() => setFilters(initialFilters)}
              >
                Clear filters
              </button>
            </div>
          </Col>
        </Row>
        <Row className="mt-2">
          <Col xs={12}>
            <BarLoader
              height={4}
              width={"100%"}
              color={"#006699"}
              loading={state.loading}
            />
          </Col>
        </Row>
        <Row className="mt-2 scroll" style={{ overflowX: "auto" }}>
          <Col xs={12} style={{ minHeight: "520px" }}>
            <table className="table-applications">
              <colgroup>
                <col span={1} style={{ width: "40%" }} />
                <col span={1} style={{ width: "30%" }} />
                <col span={1} style={{ width: "20%" }} />
                <col span={1} style={{ width: "10%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Country</th>
                  <th>Preview</th>
                </tr>
              </thead>
              <tbody>
                {paginate(
                  state.members,
                  pagination.page_number,
                  ITEM_COUNT_IN_PAGE
                ).map((member: TeamMemberData, index: number) => (
                  <tr key={index}>
                    <td>
                      <Highlighter
                        highlightClassName="highlightClass"
                        searchWords={[filters.memberName || ""]}
                        highlightTag={({ children, highlightIndex }: any) => {
                          return (
                            <span className="highlightClass">{children}</span>
                          );
                        }}
                        autoEscape={true}
                        textToHighlight={member.fullName || ""}
                      />
                    </td>
                    <td>{member.email}</td>
                    <td>{getCountryString(member.country)}</td>
                    <td>
                      <button
                        className="app-button-small"
                        onClick={() => {
                          app.pushBackURL("/dashboard/teams/members");
                          push("/dashboard/teams/members/" + member.id);
                        }}
                      >
                        Preview
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Col>
          <Col xs={12}>
            <Pagination
              pInfo={{
                page: pagination.page_number,
                totalPages: pagination.total_pages,
              }}
              onPageChanged={(page) => {
                setPagination({ ...pagination, page_number: page });
              }}
            />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Members;
